import { c as _c } from "react/compiler-runtime";
import type { FootprintVariant } from '@onefootprint/footprint-js-legacy';
import { cx } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import use100vh from './hooks/use-100vh';
type FullHeightContainerProps = HTMLAttributes<HTMLDivElement> & {
  variant?: FootprintVariant;
  hasBorderRadius?: boolean;
  isSandbox?: boolean;
  fitContent?: boolean;
  fullBleed?: boolean;
};
const INLINE_FIT_CONTENT_MIN_HEIGHT = 550;

// In iPhones, with the recent changes to move the URL bar and buttons
// to the bottom of the browser page (as overlays), 100vh no longer
// refers to the height of the screen.
// See: https://bugs.webkit.org/show_bug.cgi?id=141832#c5
// Apple engineers replied saying 'it is a feature, not a bug'.
// This component resizes itself to the actual height of the screen.
const FullHeightContainer = forwardRef<HTMLDivElement, FullHeightContainerProps>((t0, ref) => {
  const $ = _c(19);
  const {
    variant: t1,
    id,
    hasBorderRadius,
    children,
    isSandbox: t2,
    fitContent: t3,
    fullBleed: t4
  } = t0;
  const variant = t1 === undefined ? "modal" : t1;
  const isSandbox = t2 === undefined ? false : t2;
  const fitContent = t3 === undefined ? false : t3;
  const fullBleed = t4 === undefined ? false : t4;
  const viewportHeight = use100vh();
  const height = viewportHeight ? `${viewportHeight}px` : "100vh";
  const isInlineFitContent = variant === "inline" && fitContent;
  let t5;
  if ($[0] !== isInlineFitContent || $[1] !== variant) {
    t5 = () => {
      if (variant === "inline") {
        return isInlineFitContent ? cx("h-auto w-screen") : cx("h-full w-screen");
      }
      if (variant === "drawer") {
        return cx("w-full", "md:h-screen md:max-h-none md:fixed md:right-0");
      }
      return cx("w-screen", "md:h-auto");
    };
    $[0] = isInlineFitContent;
    $[1] = variant;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const getVariantClasses = t5;
  let dynamicStyles;
  if ($[3] !== height || $[4] !== isInlineFitContent || $[5] !== variant) {
    dynamicStyles = {};
    if (variant === "modal") {
      (dynamicStyles as Record<string, string>)["--mobile-height"] = height;
    } else {
      if (variant === "drawer") {
        dynamicStyles.height = height;
      } else {
        if (isInlineFitContent) {
          dynamicStyles.minHeight = `${INLINE_FIT_CONTENT_MIN_HEIGHT}px`;
        }
      }
    }
    $[3] = height;
    $[4] = isInlineFitContent;
    $[5] = variant;
    $[6] = dynamicStyles;
  } else {
    dynamicStyles = $[6];
  }
  let t6;
  if ($[7] !== getVariantClasses) {
    t6 = cx("flex flex-col overflow-y-auto relative", getVariantClasses(), "data-[scroll-lock=\"true\"]:overflow-y-hidden");
    $[7] = getVariantClasses;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = isSandbox || undefined;
  const t8 = hasBorderRadius || undefined;
  const t9 = fullBleed || undefined;
  let t10;
  if ($[9] !== children || $[10] !== dynamicStyles || $[11] !== id || $[12] !== ref || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9 || $[17] !== variant) {
    t10 = <div id={id} ref={ref} className={t6} data-variant={variant} data-sandbox={t7} data-has-border-radius={t8} data-full-bleed={t9} style={dynamicStyles}>{children}</div>;
    $[9] = children;
    $[10] = dynamicStyles;
    $[11] = id;
    $[12] = ref;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = variant;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
});
export default FullHeightContainer;